import React, { useEffect, useState, useRef } from "react";
import api, { fileUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { UploadCloud, RefreshCcw, Trash2, AlertTriangle } from "lucide-react";

// Aspect ratio abaixo do qual a logo é considerada visualmente pequena
// no Header/Footer (que usam altura fixa e largura auto). Logos horizontais
// tradicionais têm aspect entre 2.0 e 4.0; um aspect < 1.5 gera renderização
// minúscula (largura efetiva ~ altura CSS × aspect, ex.: 64 × 0.8 = 51 px).
const MIN_ASPECT_RATIO_ADVISORY = 1.5;

export default function AdminBrand() {
  const [b, setB] = useState(null);
  // Cache-busting: incrementa a cada operação para forçar reload do <img>
  const [bust, setBust] = useState(Date.now());

  async function load() {
    const r = await api.get("/admin/brand");
    setB(r.data);
    setBust(Date.now());
  }
  useEffect(() => { load(); }, []);

  async function upload(file, variant) {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/admin/brand/logo?variant=${variant}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(b?.[variant === "light" ? "logo_light_path" : "logo_path"]
        ? "Imagem substituída"
        : "Imagem enviada");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao enviar imagem");
    }
  }

  async function removeLogo(variant) {
    const label = variant === "light" ? "logotipo alternativo (fundo escuro)" : "logotipo principal (fundo claro)";
    if (!window.confirm(
      `Excluir o ${label}?\n\nA marca deixará de aparecer no lugar dessa variante e o site usará o fallback textual até um novo envio.`
    )) return;
    try {
      await api.delete(`/admin/brand/logo?variant=${variant}`);
      toast.success("Imagem excluída");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao excluir imagem");
    }
  }

  async function saveColors() {
    try {
      await api.patch("/admin/brand", {
        primary_color: b.primary_color,
        accent_color: b.accent_color,
      });
      toast.success("Cores atualizadas");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao salvar cores");
    }
  }

  if (!b) return null;

  return (
    <div className="max-w-3xl" data-testid="admin-brand-page">
      <div className="mb-8">
        <span className="text-[11px] uppercase tracking-[0.28em] text-slate-500">Identidade visual</span>
        <h1 className="text-3xl font-semibold text-[#0AA89C] mt-1">Marca</h1>
      </div>

      <div className="bg-white border border-slate-200 rounded-md p-6 space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <LogoSlot
            label="Logotipo principal (fundo claro)"
            path={b.logo_path}
            bust={bust}
            onUpload={(f) => upload(f, "primary")}
            onDelete={() => removeLogo("primary")}
            testid="logo-primary"
          />
          <LogoSlot
            label="Logotipo alternativo (fundo escuro)"
            path={b.logo_light_path}
            bust={bust}
            onUpload={(f) => upload(f, "light")}
            onDelete={() => removeLogo("light")}
            testid="logo-light"
            bg="dark"
          />
        </div>

        <div className="border-t border-slate-200 pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Cor primária</Label>
              <div className="flex gap-2 items-center">
                <Input type="color" value={b.primary_color} onChange={(e) => setB({ ...b, primary_color: e.target.value })} className="w-16" />
                <Input value={b.primary_color} onChange={(e) => setB({ ...b, primary_color: e.target.value })} />
              </div>
            </div>
            <div>
              <Label>Cor de destaque</Label>
              <div className="flex gap-2 items-center">
                <Input type="color" value={b.accent_color} onChange={(e) => setB({ ...b, accent_color: e.target.value })} className="w-16" />
                <Input value={b.accent_color} onChange={(e) => setB({ ...b, accent_color: e.target.value })} />
              </div>
            </div>
          </div>
          <Button className="mt-4 bg-[#0AA89C] hover:bg-[#066B63]" onClick={saveColors} data-testid="save-colors">Salvar cores</Button>
        </div>
      </div>
    </div>
  );
}

function LogoSlot({ label, path, bust, onUpload, onDelete, testid, bg = "light" }) {
  const ref = useRef();
  const [natural, setNatural] = useState(null); // {width, height, aspect}
  const [advisory, setAdvisory] = useState(null); // {aspect} — logo pequena/vertical

  // Detecta proporção do arquivo escolhido antes do upload (advisory only)
  function handleFile(file) {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      onUpload(file); // deixa o backend rejeitar
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const aspect = img.width / img.height;
      URL.revokeObjectURL(url);
      if (aspect < MIN_ASPECT_RATIO_ADVISORY) {
        const proceed = window.confirm(
          "A imagem escolhida tem proporção incompatível com o layout do site.\n\n" +
          `Dimensões: ${img.width} × ${img.height} px (proporção ${aspect.toFixed(2)}).\n\n` +
          "O Header e o Footer usam altura fixa e largura automática — logos verticais ou muito quadradas ficam pequenas e podem parecer ilegíveis.\n\n" +
          "Recomendação: use um arquivo horizontal (proporção entre 2.0 e 4.0), por exemplo 800 × 260 px.\n\n" +
          "Deseja enviar mesmo assim?"
        );
        if (!proceed) {
          if (ref.current) ref.current.value = "";
          return;
        }
      }
      onUpload(file);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      onUpload(file);
    };
    img.src = url;
  }

  // Detecta a proporção da imagem JÁ persistida para exibir alerta visível
  function handlePreviewLoaded(e) {
    const img = e.currentTarget;
    if (!img.naturalWidth || !img.naturalHeight) return;
    const aspect = img.naturalWidth / img.naturalHeight;
    setNatural({ width: img.naturalWidth, height: img.naturalHeight, aspect });
    setAdvisory(aspect < MIN_ASPECT_RATIO_ADVISORY ? { aspect } : null);
  }

  return (
    <div data-testid={`${testid}-slot`}>
      <Label>{label}</Label>
      <div
        className={`mt-2 aspect-[16/9] w-full rounded border border-dashed flex items-center justify-center overflow-hidden ${
          bg === "dark" ? "bg-[#22302E] border-white/20" : "bg-slate-50 border-slate-300"
        }`}
      >
        {path ? (
          <img
            src={`${fileUrl(path)}?v=${bust}`}
            alt=""
            className="max-h-full max-w-full object-contain"
            onLoad={handlePreviewLoaded}
            data-testid={`${testid}-preview`}
          />
        ) : (
          <div className={`text-xs uppercase tracking-[0.24em] ${bg === "dark" ? "text-slate-400" : "text-slate-500"}`}>
            Aguardando envio
          </div>
        )}
      </div>

      {/* Metadados naturais + alerta de proporção */}
      {path && natural && (
        <div className="mt-2 text-[11px] text-slate-500" data-testid={`${testid}-meta`}>
          Dimensões: {natural.width} × {natural.height} px · proporção {natural.aspect.toFixed(2)}
        </div>
      )}
      {path && advisory && (
        <div
          className="mt-2 flex items-start gap-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-800"
          data-testid={`${testid}-aspect-warning`}
        >
          <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
          <span>
            Proporção {advisory.aspect.toFixed(2)} incompatível com o layout do site (esperado ≥ {MIN_ASPECT_RATIO_ADVISORY.toFixed(1)}).
            A logomarca fica renderizada em tamanho muito reduzido no Header e Footer.
            Envie uma versão horizontal para corrigir.
          </span>
        </div>
      )}

      <input
        ref={ref}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/svg+xml"
        className="hidden"
        onChange={(e) => handleFile(e.target.files?.[0])}
        data-testid={`${testid}-file-input`}
      />

      <div className="mt-2 flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => ref.current?.click()}
          data-testid={path ? `${testid}-replace` : testid}
        >
          {path ? <><RefreshCcw className="w-4 h-4 mr-1" /> Substituir imagem</> : <><UploadCloud className="w-4 h-4 mr-1" /> Enviar imagem</>}
        </Button>
        {path && (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
            onClick={onDelete}
            data-testid={`${testid}-delete`}
          >
            <Trash2 className="w-4 h-4 mr-1" /> Excluir imagem
          </Button>
        )}
      </div>
    </div>
  );
}
